<script setup lang="ts">
import { track } from '@/lib/usageTracker'
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { formatDistanceToNow, format } from 'date-fns'
import { Activity, RefreshCw, SlidersHorizontal } from 'lucide-vue-next'
import { Button, Badge, Pagination } from '@/components/ui'
import Dropdown, { type DropdownOption } from '@/components/Dropdown.vue'
import TaskDetailDrawer from '@/components/task/TaskDetailDrawer.vue'
import { useTaskActivityStore } from '@/stores/taskActivityStore'
import type { TaskSummaryDto } from '@/api/lytAccountingClient'

const route = useRoute()
const router = useRouter()
const taskActivity = useTaskActivityStore()
const tasks = computed<TaskSummaryDto[]>(() => taskActivity.recentListTasks)
const selectedTask = ref<TaskSummaryDto | null>(null)
const drawerOpen = ref(false)
const loading = computed(() => taskActivity.recentListLoading)
let unsubscribeRecent: (() => void) | null = null

// --- Load-more state (per tasks-accordion) ---
// loadedLimit is the cumulative limit sent to the backend on each Load more.
// lastUpdatedAt is the cursor (updatedAt of the oldest row we have).
// canLoadMore flips to false once a response returns fewer than `loadedLimit`
// rows, signaling no more pages. The Pagination component is fed a sentinel
// `total` (loadedLimit + 1 when more is available, loadedLimit otherwise) so
// its `canLoadMore` math works without exposing the cursor publicly.
const loadedLimit = ref(50)
const lastUpdatedAt = ref<string | null>(null)
const canLoadMore = ref(true)

// Status bucket table: user-meaningful groups → canonical raw status sent to
// the backend's getRecentTasks({ status }) filter. `rawStatus: null` clears
// the status filter entirely. `queued` / `waiting` / `leased` are pre-runway
// states the recent endpoint rarely surfaces, so the "Active" bucket maps
// to the canonical in-flight `running` value. `completed_with_errors` is
// folded into the "Completed" bucket because the backend's status filter
// only accepts a single raw value.
type StatusBucketKey = 'all' | 'active' | 'completed' | 'failed' | 'dead_lettered' | 'cancelled'

interface StatusBucket {
  key: StatusBucketKey
  label: string
  rawStatus: string | null
}

const STATUS_BUCKETS: readonly StatusBucket[] = [
  { key: 'all',           label: 'All',           rawStatus: null },
  { key: 'active',        label: 'Active',        rawStatus: 'running' },
  { key: 'completed',     label: 'Completed',     rawStatus: 'completed' },
  { key: 'failed',        label: 'Failed',        rawStatus: 'failed' },
  { key: 'dead_lettered', label: 'Dead-lettered', rawStatus: 'dead_lettered' },
  { key: 'cancelled',     label: 'Cancelled',     rawStatus: 'cancelled' },
] as const

const ENTITY_OPTIONS_SEED = ['STOCK', 'ITEM', 'CUSTOMER', 'INVOICE', 'PAYMENT'] as const

const filters = computed(() => ({
  entityType: (route.query.entityType as string) || undefined,
  status: (route.query.status as string) || undefined,
  limit: loadedLimit.value,
}))

const activeFilterCount = computed(() => {
  let count = 0
  if (filters.value.entityType) count++
  if (filters.value.status) count++
  return count
})

const activeStatusBucket = computed<StatusBucketKey>(() => {
  const current = (route.query.status as string) || ''
  const match = STATUS_BUCKETS.find(b => b.rawStatus === current)
  return match?.key ?? 'all'
})

const entityOptions = computed<DropdownOption[]>(() => {
  const fromTasks = new Set(taskActivity.recentListTasks.map(t => t.entityType))
  const merged = Array.from(new Set([...ENTITY_OPTIONS_SEED, ...fromTasks]))
  return [
    { key: '', label: 'All entities', meta: 'No filter' },
    ...merged.map(t => ({ key: t, label: t })),
  ]
})

const entityTriggerLabel = computed(() =>
  filters.value.entityType ? filters.value.entityType : 'All entities',
)

const statusVariant = (status: string): 'default' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'completed': return 'success'
    case 'failed': return 'error'
    case 'dead_lettered': return 'error'
    case 'in_progress': return 'warning'
    default: return 'default'
  }
}

async function fetchTasks() {
  track('btn:RefreshTasks', 'click')
  loadedLimit.value = 50
  lastUpdatedAt.value = null
  canLoadMore.value = true
  await taskActivity.refreshRecent(filters.value, true)
}

// NOTE: `loadMore` is a windowed re-fetch, not a true append. `refreshRecent`
// overwrites `recentListTasks`, so the first click of Load more swaps the
// page for the 50 older tasks rather than accumulating. A true append
// requires a `page=N&size=M` style endpoint with server-side cursor resolution
// (tracked as a follow-up). The cursor (`lastUpdatedAt`) is still updated
// here so the request advances; the visual effect is a "jump to older page".
async function loadMore() {
  if (!canLoadMore.value || loading.value) return
  const next = {
    ...filters.value,
    limit: loadedLimit.value + 50,
    beforeTimestamp: lastUpdatedAt.value ?? undefined,
  }
  const before = tasks.value.length
  await taskActivity.refreshRecent(next, true)
  if (tasks.value.length - before < loadedLimit.value) canLoadMore.value = false
  lastUpdatedAt.value = tasks.value.at(-1)?.updatedAt ?? lastUpdatedAt.value
  loadedLimit.value += 50
}

function openTask(task: TaskSummaryDto) {
  track('btn:OpenTask', 'click')
  selectedTask.value = task
  drawerOpen.value = true
}

// Children are never top-level. Parents carry totalChildren > 0 and surface
// a clickable chevron that toggles their children. Orphans (children whose
// parent is not in the loaded window) are dropped from the top level.
// For a parent: {parent: <parent task>, tasks: <child tasks>}.
// For an independent (no parent, no children): {parent: null, tasks: [<itself>]}.
type TaskGroup = { parent: TaskSummaryDto | null, tasks: TaskSummaryDto[] }
const visibleTaskGroups = computed<TaskGroup[]>(() => {
  const all = tasks.value
  const topLevel = all.filter(t => !t.parentTaskId)
  const childCountByParent = new Map<number, TaskSummaryDto[]>()
  for (const t of all) {
    if (t.parentTaskId != null) {
      const list = childCountByParent.get(t.parentTaskId) ?? []
      list.push(t)
      childCountByParent.set(t.parentTaskId, list)
    }
  }
  return topLevel.map<TaskGroup>(top => {
    const isParent = (top.totalChildren ?? 0) > 0
    if (isParent) {
      return { parent: top, tasks: childCountByParent.get(top.taskId) ?? [] }
    }
    // Independent: render the task itself, no children, no chevron.
    return { parent: null, tasks: [top] }
  })
})
const expandedTaskIds = ref<Set<number>>(new Set())
function toggleExpand(taskId: number) {
  const next = new Set(expandedTaskIds.value)
  if (next.has(taskId)) next.delete(taskId)
  else next.add(taskId)
  expandedTaskIds.value = next
}
function isExpanded(taskId: number | null | undefined): boolean {
  return taskId != null && expandedTaskIds.value.has(taskId)
}

function setStatusBucket(bucket: StatusBucketKey) {
  track('btn:TasksStatusFilter', 'click', { bucket })
  const bucketDef = STATUS_BUCKETS.find(b => b.key === bucket)!
  const next = { ...route.query }
  if (bucketDef.rawStatus === null) {
    delete next.status
  } else {
    next.status = bucketDef.rawStatus
  }
  router.push({ query: next })
}
function clearFilter(type: 'entityType' | 'status') {
  track('btn:RemoveTaskFilter', 'click')
  const query = { ...route.query }
  delete query[type]
  router.push({ query })
}

function setEntityFilter(entityType: string | null) {
  const next = { ...route.query }
  if (entityType === null || entityType === '') {
    delete next.entityType
  } else {
    next.entityType = entityType
  }
  router.push({ query: next })
}

function formatRelative(dateStr: string) {
  if (!dateStr) return 'N/A'
  return formatDistanceToNow(new Date(dateStr), { addSuffix: true })
}

function formatAbsolute(dateStr: string) {
  if (!dateStr) return 'N/A'
  return format(new Date(dateStr), "MMM d, yyyy 'at' h:mm a")
}

function resubscribeRecent() {
  unsubscribeRecent?.()
  unsubscribeRecent = taskActivity.subscribeRecent(filters.value)
}

onMounted(() => {
  resubscribeRecent()
})

onUnmounted(() => {
  unsubscribeRecent?.()
  unsubscribeRecent = null
})

watch(() => route.query, () => resubscribeRecent())
</script>

<template>
  <div class="app-page">
    <!-- Page header -->
    <section class="app-page-header">
      <div class="app-page-heading">
        <div class="app-page-eyebrow">
          <Activity class="h-4 w-4" />
          <span>Sync operations</span>
        </div>
        <h1 class="app-page-title">Task history</h1>
        <p class="app-page-subtitle">
          Ledger of sync operations. Click any row to inspect task details and error context.
        </p>
      </div>
      <div class="app-page-actions">
        <Button
          variant="secondary"
          size="sm"
          @click="fetchTasks"
          :loading="loading"
        >
          <RefreshCw class="h-4 w-4" />
          Refresh
        </Button>
      </div>
    </section>

    <!-- Active filters -->
    <div v-if="activeFilterCount > 0" class="filter-rail">
      <div class="filter-rail-label">
        <SlidersHorizontal class="h-3.5 w-3.5" />
        <span>Filters</span>
      </div>
      <div class="filter-chips">
        <span v-if="filters.entityType" class="chip">
          <span class="chip-key">Entity</span>
          <span class="chip-val">{{ filters.entityType }}</span>
          <button class="chip-remove" @click="clearFilter('entityType')" aria-label="Remove entity filter">&#10005;</button>
        </span>
        <span v-if="filters.status" class="chip">
          <span class="chip-key">Status</span>
          <span class="chip-val">{{ filters.status }}</span>
          <button class="chip-remove" @click="clearFilter('status')" aria-label="Remove status filter">&#10005;</button>
        </span>
      </div>
      <button v-if="activeFilterCount > 1" class="clear-all" @click="() => { track('btn:ClearAllTaskFilters', 'click'); router.push({ query: {} }) }">
        Clear all
      </button>
    </div>

    <!-- Filter bar (always rendered) -->
    <section class="filter-bar" aria-label="Task filters">
      <div class="filter-bar-group" role="tablist" aria-label="Status">
        <button
          v-for="bucket in STATUS_BUCKETS"
          :key="bucket.key"
          type="button"
          role="tab"
          :aria-selected="activeStatusBucket === bucket.key"
          class="status-tab"
          :class="{ 'status-tab--active': activeStatusBucket === bucket.key }"
          @click="setStatusBucket(bucket.key)"
        >
          {{ bucket.label }}
        </button>
      </div>

      <Dropdown
        :options="entityOptions"
        :model-value="filters.entityType ?? ''"
        :trigger-label="entityTriggerLabel"
        trigger-meta="Entity"
        empty-message="No entity types available"
        @select="setEntityFilter"
      />
    </section>

    <!-- Task list -->
    <div class="task-list">
      <!-- Loading skeleton -->
      <div v-if="loading && tasks.length === 0" class="task-skeleton-list">
        <div v-for="i in 5" :key="i" class="task-skeleton-row">
          <div class="skeleton skeleton-badge"></div>
          <div class="skeleton skeleton-text"></div>
          <div class="skeleton skeleton-text skeleton-text-sm"></div>
          <div class="skeleton skeleton-text skeleton-text-xs"></div>
        </div>
      </div>

      <!-- Task rows (grouped: parents + independents; children hidden until expanded) -->
      <div v-else-if="visibleTaskGroups.length > 0">
        <div v-for="group in visibleTaskGroups" :key="group.parent?.taskId ?? (group.tasks[0]?.taskId ?? 0)">
          <!-- Parent row -->
          <div
            v-if="group.parent"
            class="task-row task-row--parent"
            role="button"
            :aria-expanded="isExpanded(group.parent.taskId)"
            tabindex="0"
            @click="openTask(group.parent!)"
            @keydown.enter="openTask(group.parent!)"
          >
            <button
              type="button"
              class="task-row-chevron task-row-chevron--parent"
              :class="{ 'is-expanded': isExpanded(group.parent.taskId) }"
              :aria-label="isExpanded(group.parent.taskId) ? 'Collapse child tasks' : 'Expand child tasks'"
              @click.stop="toggleExpand(group.parent.taskId)"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div class="task-row-status">
              <Badge :variant="statusVariant(group.parent.status)" size="sm">
                {{ group.parent.status }}
              </Badge>
            </div>
            <div class="task-row-entity">
              <span class="entity-type-label">{{ group.parent.entityType }}</span>
              <span class="entity-ext-id">{{ group.parent.externalId || '—' }}</span>
            </div>
            <div class="task-row-meta">
              <span class="task-id-mono" :title="String(group.parent.taskId)">{{ group.parent.taskId }}</span>
              <span class="task-attempts">{{ group.parent.attemptCount }}x</span>
              <span class="task-updated" :title="formatAbsolute(group.parent.updatedAt)">
                {{ formatRelative(group.parent.updatedAt) }}
              </span>
              <span class="task-child-count" v-if="(group.parent.totalChildren ?? 0) > 0">
                {{ group.parent.completedChildren ?? 0 }}/{{ group.parent.totalChildren }} children
              </span>
            </div>
          </div>

          <!-- Independent task (no children, no parent) -->
          <div
            v-else-if="group.tasks.length > 0"
            class="task-row"
            role="button"
            tabindex="0"
            @click="openTask(group.tasks[0])"
            @keydown.enter="openTask(group.tasks[0])"
          >
            <div class="task-row-status">
              <Badge :variant="statusVariant(group.tasks[0].status)" size="sm">
                {{ group.tasks[0].status }}
              </Badge>
            </div>
            <div class="task-row-entity">
              <span class="entity-type-label">{{ group.tasks[0].entityType }}</span>
              <span class="entity-ext-id">{{ group.tasks[0].externalId || '—' }}</span>
            </div>
            <div class="task-row-meta">
              <span class="task-id-mono" :title="String(group.tasks[0].taskId)">{{ group.tasks[0].taskId }}</span>
              <span class="task-attempts">{{ group.tasks[0].attemptCount }}x</span>
              <span class="task-updated" :title="formatAbsolute(group.tasks[0].updatedAt)">
                {{ formatRelative(group.tasks[0].updatedAt) }}
              </span>
            </div>
          </div>

          <!-- Child rows (rendered when the parent is expanded) -->
          <template v-if="isExpanded(group.parent?.taskId ?? null)">
          <div
            v-for="child in group.tasks"
            :key="`child-${child.taskId}`"
            class="task-row task-row--child"
            tabindex="0"
            @click="openTask(child)"
            @keydown.enter="openTask(child)"
          >
            <div class="task-row-status">
              <Badge :variant="statusVariant(child.status)" size="sm">
                {{ child.status }}
              </Badge>
            </div>
            <div class="task-row-entity">
              <span class="entity-type-label">{{ child.entityType }}</span>
              <span class="entity-ext-id">{{ child.externalId || '—' }}</span>
            </div>
            <div class="task-row-meta">
              <span class="task-id-mono" :title="String(child.taskId)">{{ child.taskId }}</span>
              <span class="task-attempts">{{ child.attemptCount }}x</span>
              <span class="task-updated" :title="formatAbsolute(child.updatedAt)">
                {{ formatRelative(child.updatedAt) }}
              </span>
            </div>
          </div>
          </template>
        </div>
      </div>
      <!-- Orphan-only: all loaded rows are children of parents outside the window -->
      <div v-else-if="tasks.length > 0" class="app-empty-state">
        <div class="app-empty-state-symbol">
          <Activity class="h-5 w-5" />
        </div>
        <p class="app-empty-state-title">All recent operations are children of tasks outside the current window</p>
        <p class="app-empty-state-copy">
          <button class="inline-link" @click="fetchTasks">Refresh</button>
          to load the latest activity.
        </p>
      </div>
      <!-- No tasks -->
      <div v-else class="app-empty-state">
        <div class="app-empty-state-symbol">
          <Activity class="h-5 w-5" />
        </div>
        <p class="app-empty-state-title">No tasks match the current filters</p>
        <p class="app-empty-state-copy">
          Try adjusting the entity type or status filter, or
          <button class="inline-link" @click="() => { track('btn:ClearAllTaskFilters', 'click'); router.push({ query: {} }) }">clear all filters</button>
          to see all activity.
        </p>
      </div>
    </div>

    <!-- Load more (parent/child grouping renders inside .task-list above) -->
    <Pagination
      v-if="tasks.length > 0"
      mode="load-more"
      :page="1"
      :page-size="loadedLimit"
      :total="canLoadMore ? loadedLimit + 1 : loadedLimit"
      :loading="loading"
      label="operations"
      track-label="btn:TasksLoadMore"
      @load-more="loadMore"
    />

    <TaskDetailDrawer
      :task="selectedTask"
      :open="drawerOpen"
      @action-complete="fetchTasks"
      @close="drawerOpen = false"
    />
  </div>
</template>

<style scoped>
/* ── Tokens (referencing main.css primitives) ── */
.task-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-surface);
}

/* ── Filter rail ── */
.filter-rail {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.filter-rail-label {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-ink-soft);
}

.filter-chips {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  background: var(--color-surface-muted);
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  color: var(--color-ink);
}

.chip-key {
  font-weight: 600;
  color: var(--color-ink-soft);
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.chip-val {
  font-weight: 500;
}

.chip-remove {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--color-ink-soft);
  font-size: 0.65rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.chip-remove:hover { opacity: 1; }

.clear-all {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.75rem;
  color: var(--color-primary);
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* ── Task rows ── */
.task-row {
  display: grid;
  grid-template-columns: 6.5rem minmax(0, 1.2fr) minmax(0, 1fr) auto;
  gap: 0 0.75rem;
  align-items: center;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid var(--color-border-soft);
  cursor: pointer;
  transition: background 0.12s ease;
  outline: none;
}

.task-row:last-child { border-bottom: none; }

.task-row:hover,
.task-row:focus-visible {
  background: var(--color-surface-muted);
}

.task-row:focus-visible {
  box-shadow: inset 0 0 0 2px var(--color-primary);
}

/* Status column */
.task-row-status {
  display: flex;
  align-items: center;
}

/* Entity column */
.task-row-entity {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.entity-type-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.entity-ext-id {
  font-size: 0.72rem;
  color: var(--color-ink-soft);
  font-family: 'SF Mono', 'Fira Code', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Meta column */
.task-row-meta {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.task-id-mono {
  font-size: 0.68rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  color: var(--color-ink-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-attempts {
  font-size: 0.7rem;
  color: var(--color-ink-soft);
}

.task-updated {
  font-size: 0.7rem;
  color: var(--color-ink-soft);
}

/* Chevron */
.task-row-chevron {
  color: var(--color-ink-soft);
  opacity: 0;
  transition: opacity 0.12s ease, transform 0.15s ease;
  display: flex;
  align-items: center;
  background: transparent;
  border: 0;
  padding: 0.2rem;
  margin-right: 0.4rem;
  cursor: pointer;
}

.task-row-chevron--parent {
  opacity: 1;
}

.task-row-chevron--parent.is-expanded {
  transform: rotate(90deg);
}

.task-row:hover .task-row-chevron,
.task-row:focus-visible .task-row-chevron {
  opacity: 1;
}

/* Parent / child row variants (tasks-accordion) */
.task-row--child {
  padding-left: 2.25rem;
  background: var(--color-surface-muted);
}

.task-child-count {
  font-size: 0.7rem;
  color: var(--color-ink-soft);
  margin-left: 0.4rem;
  padding: 0.1rem 0.4rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  border: 1px solid var(--color-border-soft);
}

/* ── Empty state ── */
.inline-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--color-primary);
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
}



/* ── Loading skeleton ── */
.task-skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.task-skeleton-row {
  display: grid;
  grid-template-columns: 6.5rem minmax(0, 1.2fr) minmax(0, 1fr) auto;
  gap: 0 0.75rem;
  align-items: center;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid var(--color-border-soft);
}

.skeleton {
  background: linear-gradient(90deg, var(--color-surface-muted) 25%, var(--color-border-soft) 50%, var(--color-surface-muted) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: var(--radius-sm);
  height: 0.85rem;
}

.skeleton-badge { width: 5rem; height: 1.4rem; border-radius: 999px; }
.skeleton-text { width: 100%; }
.skeleton-text-sm { height: 0.7rem; }
.skeleton-text-xs { height: 0.6rem; width: 80%; }

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Tablet ── */
@media (max-width: 768px) {
  .task-row {
    grid-template-columns: 5.5rem minmax(0, 1fr) auto;
    gap: 0 0.6rem;
    padding: 0.6rem 0.75rem;
  }

  .task-row-meta {
    display: none;
  }
}

/* ── Mobile ── */
@media (max-width: 480px) {
  .task-row {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0 0.5rem;
    padding: 0.6rem 0.65rem;
  }

  .task-row-status {
    display: none;
  }

  .entity-type-label {
    font-size: 0.8rem;
  }

  .entity-ext-id {
    font-size: 0.68rem;
  }
}

/* ── Filter bar ── */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}
.filter-bar-group {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}
.status-tab {
  padding: 0.375rem 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface);
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-ink-soft);
  cursor: pointer;
  transition: all 0.15s;
}
.status-tab:hover {
  background: var(--color-surface-muted);
}
.status-tab--active {
  background: var(--color-ink);
  color: var(--color-surface);
  border-color: var(--color-border-strong);
}
</style>
